import { AxiosError } from 'axios';

export const getAxiosError = (error: AxiosError<any>) => {
  // Handle network errors (like when network is blocked)
  if (!error.response) {
    return error.message || 'Network error occurred';
  }

  const data = error.response?.data;
  if (!data) {
    return error.message || 'Unknown error occurred';
  }

  if (typeof data === 'string') {
    try {
      const parsedData = JSON.parse(data);
      return extractFirstErrorMessageOfObject(parsedData);
    } catch (error) {
      return data;
    }
  }

  return extractFirstErrorMessageOfObject(data);
};

// Standard keys whose values are already human-readable messages
const STANDARD_ERROR_KEYS = new Set(['detail', 'error', 'message', 'non_field_errors']);

const extractErrorValue = (value: any): string => {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String).join(', ');
    } catch {
      // Not JSON, return as-is
    }
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    return extractFirstErrorMessageOfObject(value);
  }
  return String(value);
};

const extractFirstErrorMessageOfObject = (data?: any): string => {
  if (!data || typeof data !== 'object') {
    return 'Unknown error occurred';
  }

  const keys = Object.keys(data);
  if (keys.length === 0) return 'Something went wrong';

  // If a standard key exists, return its value directly (no field prefix)
  for (const key of keys) {
    if (STANDARD_ERROR_KEYS.has(key)) {
      return extractErrorValue(data[key]);
    }
  }

  // Field-level validation errors — include field names for context
  const fieldErrors = keys.map((field) => `${field}: ${extractErrorValue(data[field])}`);
  return fieldErrors.join('; ');
};
